import { useEffect, useRef } from 'react'
import { getSocket } from '../../socket/client.js'

export interface WebRtcStats {
  packetLoss: number
  jitter: number
  rtt: number
  mos: number
}

/**
 * Simplified ITU-T E-model approximation. Returns a MOS value
 * clamped to [1, 4.5].
 *
 * Inputs:
 *  - packetLoss: 0..1 (fraction)
 *  - jitter: milliseconds
 *  - rtt: milliseconds
 */
export function estimateMos(packetLoss: number, jitter: number, rtt: number): number {
  // Loss is typically 0..1; the formula expects a percentage.
  const lossPct = packetLoss * 100
  const r = 93.2 - lossPct * 2.5 - jitter * 0.5 - rtt * 0.1
  if (r < 0) return 1
  if (r > 100) return 4.5
  const mos = 1 + 0.035 * r + 7e-6 * r * (r - 60) * (100 - r)
  if (mos < 1) return 1
  if (mos > 4.5) return 4.5
  return mos
}

export function useWebRtcStats(
  peerConnection: RTCPeerConnection | null,
  intervalMs = 10_000,
): void {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (!peerConnection) return

    intervalRef.current = setInterval(async () => {
      const stats = await peerConnection.getStats()
      let packetLoss = 0
      let jitter = 0
      let rtt = 0
      let hasData = false

      stats.forEach((report) => {
        const r = report as unknown as {
          type: string
          kind?: string
          packetsReceived?: number
          packetsLost?: number
          jitter?: number
          roundTripTime?: number
        }
        if (r.type === 'inbound-rtp' && r.kind === 'audio') {
          const total = (r.packetsReceived ?? 0) + (r.packetsLost ?? 0)
          packetLoss = total > 0 ? (r.packetsLost ?? 0) / total : 0
          jitter = (r.jitter ?? 0) * 1000
          hasData = true
        }
        if (r.type === 'remote-inbound-rtp') {
          rtt = (r.roundTripTime ?? 0) * 1000
        }
      })

      if (!hasData) return

      const mos = estimateMos(packetLoss, jitter, rtt)
      const socket = getSocket()
      socket.emit('webrtc:stats', { packetLoss, jitter, rtt, mos })
    }, intervalMs)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [peerConnection, intervalMs])
}
